import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Gets or creates a DOM node for portals synchronously.
 */
function getOrCreatePortalElement(id: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;

  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('div');
    el.setAttribute('id', id);
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Creates (or reuses) a DOM node for portals and returns a render helper.
 */
export function usePortal(id = 'paction-ui-portal') {
  // Use ref to store portal element - no state needed since we create it synchronously
  const portalElRef = useRef<HTMLElement | null>(null);

  const ensurePortalElement = () => {
    if (typeof document === 'undefined') return;
    // If the element was removed (e.g. by another unmount), re-create it.
    if (!portalElRef.current || !portalElRef.current.isConnected) {
      portalElRef.current = getOrCreatePortalElement(id);
    }
  };

  // Initialize synchronously on first access
  ensurePortalElement();

  useEffect(() => {
    // Intentionally do not remove the portal node on unmount.
    // Multiple UI components may hold refs to the same portal element; removing it can
    // leave other still-mounted components rendering into a detached node.
    return;
  }, []);

  const render = (children: React.ReactNode) => {
    ensurePortalElement();
    if (!portalElRef.current) return null;
    return createPortal(children, portalElRef.current);
  };

  return { render };
}
